import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Clock,
  CheckCircle2,
  Cog,
  Truck,
  PackageCheck,
  CreditCard,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { LoadingSkeleton } from "@/components/sartn/LoadingSkeleton";
import { useBusinessStore } from "@/stores/businessStore";
import { useLanguageStore } from "@/stores/languageStore";
import type { Order, OrderStatus } from "@/stores/businessStore";
import { cn } from "@/lib/cn";

function formatETB(amount: number): string {
  return `${amount.toLocaleString("en-US")} ETB`;
}

function formatTimestamp(isoString: string | undefined): string {
  if (!isoString) return "";
  return new Date(isoString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface TimelineStep {
  key: OrderStatus;
  label: string;
  icon: LucideIcon;
  timestampField: keyof Order;
}

const TIMELINE_STEPS: TimelineStep[] = [
  { key: "pending", label: "Order Placed", icon: Clock, timestampField: "createdAt" },
  { key: "confirmed", label: "Confirmed", icon: CheckCircle2, timestampField: "confirmedAt" },
  { key: "processing", label: "Processing", icon: Cog, timestampField: "processingAt" },
  { key: "shipped", label: "Shipped", icon: Truck, timestampField: "shippedAt" },
  { key: "delivered", label: "Delivered", icon: PackageCheck, timestampField: "deliveredAt" },
];

const STATUS_ORDER: OrderStatus[] = [
  "pending",
  "confirmed",
  "processing",
  "shipped",
  "delivered",
];

function getStatusIndex(status: OrderStatus): number {
  return STATUS_ORDER.indexOf(status);
}

function TimelineStepRow({
  step,
  order,
  stepIndex,
}: {
  step: TimelineStep;
  order: Order;
  stepIndex: number;
}) {
  const currentIndex = getStatusIndex(order.status);
  const isCompleted = stepIndex <= currentIndex && order.status !== "cancelled";
  const isCurrent = stepIndex === currentIndex && order.status !== "cancelled";
  const isFuture = stepIndex > currentIndex;
  const isLast = stepIndex === TIMELINE_STEPS.length - 1;

  const timestamp = order[step.timestampField] as string | undefined;
  const Icon = step.icon;

  return (
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: stepIndex * 0.08, duration: 0.25 }}
      className="flex gap-3"
    >
      <div className="flex flex-col items-center">
        <div
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full",
            isCurrent && "bg-[var(--color-primary)] ring-2 ring-[var(--color-primary)]/30",
            isCompleted && !isCurrent && "bg-green-600",
            isFuture && "bg-[var(--color-surface)] border border-[var(--color-border)]",
          )}
        >
          <Icon
            className={cn(
              "h-4 w-4",
              isCompleted ? "text-white" : "text-[var(--color-text-muted)]",
            )}
          />
        </div>
        {!isLast && (
          <div
            className={cn(
              "mt-1 h-6 w-0.5",
              isCompleted && stepIndex < currentIndex
                ? "bg-green-600"
                : "bg-[var(--color-border)]",
            )}
          />
        )}
      </div>

      <div className="pb-4">
        <span
          className={cn(
            "text-sm font-medium",
            isCompleted ? "text-[var(--color-text)]" : "text-[var(--color-text-muted)]",
          )}
        >
          {step.label}
        </span>
        {timestamp && isCompleted && (
          <p className="text-xs text-[var(--color-text-secondary)]">
            {formatTimestamp(timestamp)}
          </p>
        )}
      </div>
    </motion.div>
  );
}

function OrderInfoCard({ order }: { order: Order }) {
  return (
    <div className="mb-4 rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold text-[var(--color-text)]">
          {order.contactName}
        </span>
        <StatusBadgeInline status={order.status} />
      </div>
      <div className="space-y-1.5 text-sm text-[var(--color-text-secondary)]">
        <p>Product: {order.productName}</p>
        <p>Quantity: {order.quantity}</p>
        <p className="font-semibold text-[var(--color-text)]">
          Total: {formatETB(order.totalPrice)}
        </p>
      </div>
    </div>
  );
}

function StatusBadgeInline({ status }: { status: OrderStatus }) {
  const colorMap: Record<OrderStatus, string> = {
    pending: "bg-amber-50 text-amber-700",
    confirmed: "bg-blue-50 text-blue-700",
    processing: "bg-indigo-50 text-indigo-700",
    shipped: "bg-purple-50 text-purple-700",
    delivered: "bg-green-50 text-green-700",
    cancelled: "bg-red-50 text-red-700",
  };

  return (
    <span
      className={cn(
        "rounded-[var(--radius-full)] px-2 py-0.5 text-xs font-medium capitalize",
        colorMap[status],
      )}
    >
      {status}
    </span>
  );
}

function PaymentSection({ order }: { order: Order }) {
  const language = useLanguageStore((state) => state.language);

  return (
    <div className="mb-4 rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-[var(--color-text)]">Payment</h3>
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-[var(--color-text-secondary)]">
          <CreditCard className="h-4 w-4" />
          <span>{order.paymentMethod}</span>
        </div>
        <div className="flex items-center gap-2 text-[var(--color-text-secondary)]">
          <span>Amount: {formatETB(order.totalPrice)}</span>
        </div>
        <div className="flex items-center gap-2">
          {order.paymentVerified ? (
            <>
              <ShieldCheck className="h-4 w-4 text-green-600" />
              <span className="text-xs font-medium text-green-600">Verified</span>
            </>
          ) : (
            <>
              <ShieldAlert className="h-4 w-4 text-amber-600" />
              <span className="text-xs font-medium text-amber-600">
                {language === "am" ? "የክፍያ ማመሳሰል ገና ነው" : "Payment sync pending"}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionButtons({
  order,
  trackingInput,
  onTrackingChange,
  onUpdateStatus,
}: {
  order: Order;
  trackingInput: string;
  onTrackingChange: (value: string) => void;
  onUpdateStatus: (status: OrderStatus) => void;
}) {
  function navigateBack() {
    window.location.hash = "/business";
  }

  if (order.status === "cancelled" || order.status === "delivered") {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      {order.status === "pending" && (
        <>
          <motion.button
            type="button"
            whileTap={{ scale: 0.97 }}
            onClick={() => onUpdateStatus("confirmed")}
            className="w-full rounded-[var(--radius-md)] bg-[var(--color-primary)] py-3
                       text-sm font-semibold text-white transition-colors"
          >
            Confirm Order
          </motion.button>
          <motion.button
            type="button"
            whileTap={{ scale: 0.97 }}
            onClick={() => {
              onUpdateStatus("cancelled");
              navigateBack();
            }}
            className="w-full rounded-[var(--radius-md)] bg-red-50 py-3
                       text-sm font-semibold text-red-700 transition-colors"
          >
            Cancel Order
          </motion.button>
        </>
      )}

      {order.status === "confirmed" && (
        <motion.button
          type="button"
          whileTap={{ scale: 0.97 }}
          onClick={() => onUpdateStatus("processing")}
          className="w-full rounded-[var(--radius-md)] bg-[var(--color-primary)] py-3
                     text-sm font-semibold text-white transition-colors"
        >
          Start Processing
        </motion.button>
      )}

      {order.status === "processing" && (
        <>
          <input
            type="text"
            value={trackingInput}
            onChange={(e) => onTrackingChange(e.target.value)}
            placeholder="Tracking number (optional)"
            className={cn(
              "w-full rounded-[var(--radius-md)] bg-[var(--color-surface)]",
              "px-3 py-2.5 text-sm text-[var(--color-text)] shadow-sm",
              "placeholder:text-[var(--color-text-muted)]",
              "focus:ring-1 focus:ring-[var(--color-primary)] focus:outline-none",
            )}
          />
          <motion.button
            type="button"
            whileTap={{ scale: 0.97 }}
            onClick={() => onUpdateStatus("shipped")}
            className="w-full rounded-[var(--radius-md)] bg-[var(--color-primary)] py-3
                       text-sm font-semibold text-white transition-colors"
          >
            Mark Shipped
          </motion.button>
        </>
      )}

      {order.status === "shipped" && (
        <motion.button
          type="button"
          whileTap={{ scale: 0.97 }}
          onClick={() => onUpdateStatus("delivered")}
          className="w-full rounded-[var(--radius-md)] bg-green-600 py-3
                     text-sm font-semibold text-white transition-colors"
        >
          Mark Delivered
        </motion.button>
      )}
    </div>
  );
}

interface OrderDetailProps {
  orderId: string;
}

export default function OrderDetail({ orderId }: OrderDetailProps) {
  const { orders, fetchOrders, updateOrderStatus } = useBusinessStore();
  const [trackingInput, setTrackingInput] = useState("");

  useEffect(() => {
    if (orders.length === 0) {
      fetchOrders();
    }
  }, [orders.length, fetchOrders]);

  const order = orders.find((o) => o.id === orderId);

  function navigateBack() {
    window.location.hash = "/business";
  }

  async function handleUpdateStatus(newStatus: OrderStatus) {
    const trackingNumber = newStatus === "shipped" ? trackingInput.trim() : undefined;
    await updateOrderStatus(orderId, newStatus, trackingNumber || undefined);
  }

  if (orders.length === 0) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Order" onBack={navigateBack} />
        <LoadingSkeleton variant="card" count={3} className="px-4 pt-4" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Order" onBack={navigateBack} />
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-[var(--color-text-muted)]">Order not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title={`Order #${order.id}`} onBack={navigateBack} />

      <motion.main
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="flex-1 px-4 pb-20 pt-4"
      >
        <OrderInfoCard order={order} />

        <div className="mb-4 rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-4 shadow-sm">
          <h3 className="mb-4 text-sm font-semibold text-[var(--color-text)]">
            Order Timeline
          </h3>
          {order.status === "cancelled" ? (
            <p className="text-sm text-red-600">This order was cancelled.</p>
          ) : (
            TIMELINE_STEPS.map((step, index) => (
              <TimelineStepRow
                key={step.key}
                step={step}
                order={order}
                stepIndex={index}
              />
            ))
          )}
          {order.trackingNumber && (
            <div className="mt-2 flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
              <Truck className="h-4 w-4" />
              <span>Tracking: {order.trackingNumber}</span>
            </div>
          )}
        </div>

        <PaymentSection order={order} />

        <ActionButtons
          order={order}
          trackingInput={trackingInput}
          onTrackingChange={setTrackingInput}
          onUpdateStatus={handleUpdateStatus}
        />
      </motion.main>
    </div>
  );
}
